import type { ReactNode } from "react";

type Tilt = "left" | "center" | "right";
type Size = "sm" | "md" | "lg";

interface PhoneFrameProps {
	children?: ReactNode;
	tilt?: Tilt;
	size?: Size;
	className?: string;
	label?: string;
}

const TILT_CLASS: Record<Tilt, string> = {
	left: "-rotate-6",
	center: "rotate-0",
	right: "rotate-6",
};

const SIZE_CLASS: Record<Size, string> = {
	sm: "h-[560px] w-[280px] rounded-[2.5rem] border-[8px]",
	md: "h-[640px] w-[320px] rounded-[3rem] border-[10px]",
	lg: "h-[720px] w-[360px] rounded-[3.25rem] border-[12px]",
};

const SCREEN_RADIUS: Record<Size, string> = {
	sm: "rounded-[1.85rem]",
	md: "rounded-[2.25rem]",
	lg: "rounded-[2.4rem]",
};

const NOTCH_SIZE: Record<Size, string> = {
	sm: "h-6 w-24",
	md: "h-7 w-32",
	lg: "h-8 w-36",
};

export function PhoneFrame({ children, tilt = "center", size = "md", className = "", label }: PhoneFrameProps) {
	return (
		<div role="img" aria-label={label ?? "Mobile app preview"} className={`relative ${TILT_CLASS[tilt]} ${className}`}>
			<div className={`relative overflow-hidden border-neutral-900 bg-neutral-900 shadow-2xl shadow-accent/30 ${SIZE_CLASS[size]}`}>
				<div
					aria-hidden="true"
					className={`pointer-events-none absolute inset-x-0 top-0 z-30 mx-auto flex -translate-y-1 items-center justify-center rounded-b-2xl bg-neutral-900 ${NOTCH_SIZE[size]}`}
				/>
				<div className={`absolute inset-0 overflow-hidden bg-white ${SCREEN_RADIUS[size]}`}>{children}</div>
				<div aria-hidden="true" className={`pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10 ${SCREEN_RADIUS[size]}`} />
			</div>
		</div>
	);
}
